import { ROUTE_NAMES } from '@brc/route-names';
import { FaultStatus } from '@brc/shared/models';

export function parseNumberWithDefaultValue(param?: string, fallbackValue = 1): number {
  if (param === undefined) return fallbackValue;
  const parsed = Number(param);
  return !isNaN(parsed) ? parsed : fallbackValue;
}

export function parseNumberNullable(param?: string): number | undefined {
  if (param === undefined) return;
  const parsed = Number(param);
  return !isNaN(parsed) ? parsed : undefined;
}

export function parseNullableString(param?: string) {
  if (param === undefined) return undefined;
  const parsed = Number(param);
  return !isNaN(parsed) ? parsed : undefined;
}

export function parseNumberArray(params?: string): undefined | number[] {
  return params ? (Array.isArray(params) ? params.map(Number) : [Number(params)]) : undefined;
}

export function parseFaultStatusses(params?: string): undefined | FaultStatus[] {
  return params ? (Array.isArray(params) ? params : [params as FaultStatus]) : undefined;
}

/**
 * Extract an organization id from a route URL or fragment.
 * Supports full URLs with hash-based routing and raw fragments/paths.
 * Examples:
 *  http://.../#/fault-analysis/100258802?...
 *  /fault-analysis/100258802
 */
export function extractOrganizationId(route?: string): number | undefined {
  if (!route) return undefined;

  // If there's a hash (#), use the part after it (hash-based routing)
  const afterHash = route.includes('#') ? route.split('#')[1] : route;
  if (!afterHash) return undefined;

  // Remove query string
  const path = afterHash.split('?')[0];

  // Normalize leading slash
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const segments = normalized.split('/').filter(Boolean);

  // Expect routes like ['fault-analysis','100258802'] or ['overview','100258802']
  if (segments.length < 1) return undefined;

  // If the first segment is a number, treat it as org-first (/42/overview)
  const first = Number(segments[0]);
  if (Number.isFinite(first)) return first;

  // Otherwise expect route-first (/overview/42)
  if (segments.length >= 2) {
    const candidate = segments[1];
    const parsed = Number(candidate);
    return Number.isFinite(parsed) ? parsed : undefined;
  }

  return undefined;
}

/**
 * Extract the primary route segment (e.g. 'fault-analysis', 'overview') from a URL or fragment.
 */
export function extractRouteFromURL(route?: string): ROUTE_NAMES | undefined {
  if (!route) return undefined;

  const afterHash = route.includes('#') ? route.split('#')[1] : route;
  if (!afterHash) return undefined;

  const path = afterHash.split('?')[0];
  const normalized = path.startsWith('/') ? path : `/${path}`;
  const segments = normalized.split('/').filter(Boolean);
  if (segments.length === 0) return undefined;

  // If the first segment is numeric (org-first), return the second segment as route
  const first = Number(segments[0]);
  if (Number.isFinite(first) && segments.length >= 2) return segments[1] as ROUTE_NAMES;

  return segments[0] as ROUTE_NAMES;
}
